<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import FinancialAPI from 'dashboard/api/financial';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  formatAmount,
  formatDueDate,
  isOverdue,
  talkviaWhatsappUrl,
} from './suspendedBilling';

const { t } = useI18n();
const { isAdmin } = useAdmin();

const payments = ref([]);
const isLoading = ref(false);
const loadFailed = ref(false);
const copyingPixId = ref('');

const whatsappUrl = computed(() =>
  talkviaWhatsappUrl(t('APP_GLOBAL.ACCOUNT_SUSPENDED.WHATSAPP_MESSAGE'))
);

const loadPayments = async () => {
  if (!isAdmin.value) return;
  isLoading.value = true;
  loadFailed.value = false;
  try {
    const { data } = await FinancialAPI.openPayments();
    payments.value = data.payments || [];
  } catch {
    loadFailed.value = true;
  } finally {
    isLoading.value = false;
  }
};

const copyPix = async payment => {
  copyingPixId.value = payment.id;
  try {
    const { data } = await FinancialAPI.pix(payment.id);
    await copyTextToClipboard(data.payload);
    useAlert(t('APP_GLOBAL.ACCOUNT_SUSPENDED.PIX_COPIED'));
  } catch {
    useAlert(t('APP_GLOBAL.ACCOUNT_SUSPENDED.PIX_ERROR'));
  } finally {
    copyingPixId.value = '';
  }
};

const openLink = url => window.open(url, '_blank', 'noopener,noreferrer');

onMounted(loadPayments);
</script>

<template>
  <div
    class="flex h-full w-full justify-center overflow-y-auto bg-n-slate-2 px-4 py-10"
  >
    <div class="flex w-full max-w-xl flex-col gap-5 self-center">
      <section
        class="rounded-2xl border border-n-weak bg-n-solid-1 p-6 text-center shadow-sm dark:border-n-strong"
      >
        <span
          class="mx-auto grid size-12 place-items-center rounded-2xl bg-n-ruby-3 text-n-ruby-11"
          aria-hidden="true"
        >
          <span class="i-hugeicons-alert-02 size-6" />
        </span>
        <h1 class="mb-1 mt-4 text-xl font-semibold text-n-slate-12">
          {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.TITLE') }}
        </h1>
        <p class="mb-0 text-sm text-n-slate-11">
          {{
            isAdmin
              ? t('APP_GLOBAL.ACCOUNT_SUSPENDED.ADMIN_MESSAGE')
              : t('APP_GLOBAL.ACCOUNT_SUSPENDED.AGENT_MESSAGE')
          }}
        </p>
        <a
          v-if="isAdmin"
          :href="whatsappUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1fb957] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#25D366]"
        >
          <span class="i-simple-icons-whatsapp size-4" aria-hidden="true" />
          {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.WHATSAPP') }}
        </a>
      </section>

      <section
        v-if="isAdmin"
        class="rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm dark:border-n-strong"
        :aria-busy="isLoading"
      >
        <header
          class="flex items-center gap-2 border-b border-n-weak px-5 py-4"
        >
          <span
            class="i-hugeicons-invoice-03 size-5 text-n-slate-11"
            aria-hidden="true"
          />
          <h2 class="mb-0 text-base font-semibold text-n-slate-12">
            {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.OPEN_PAYMENTS') }}
          </h2>
        </header>

        <div
          v-if="isLoading"
          class="flex items-center justify-center gap-2 px-5 py-8 text-sm text-n-slate-11"
        >
          <Spinner />
          {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.LOADING') }}
        </div>
        <div v-else-if="loadFailed" class="px-5 py-6 text-center">
          <p class="mb-3 text-sm text-n-slate-11">
            {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.LOAD_ERROR') }}
          </p>
          <NextButton
            sm
            faded
            slate
            :label="t('APP_GLOBAL.ACCOUNT_SUSPENDED.RETRY')"
            @click="loadPayments"
          />
        </div>
        <p
          v-else-if="!payments.length"
          class="mb-0 px-5 py-6 text-center text-sm text-n-slate-11"
        >
          {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.NO_OPEN_PAYMENTS') }}
        </p>
        <ul v-else class="mb-0 divide-y divide-n-weak">
          <li
            v-for="payment in payments"
            :key="payment.id"
            class="flex flex-col gap-3 px-5 py-4"
            data-testid="open-payment"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <p
                  class="mb-0 text-lg font-semibold tabular-nums text-n-slate-12"
                >
                  {{ formatAmount(payment.value) }}
                </p>
                <p class="mb-0 text-xs text-n-slate-11">
                  {{
                    t('APP_GLOBAL.ACCOUNT_SUSPENDED.DUE_DATE', {
                      date: formatDueDate(payment.dueDate),
                    })
                  }}
                </p>
                <p
                  v-if="payment.description"
                  class="mb-0 mt-1 text-xs text-n-slate-10"
                >
                  {{ payment.description }}
                </p>
              </div>
              <span
                class="rounded-md px-2 py-1 text-xs font-medium"
                :class="
                  isOverdue(payment)
                    ? 'bg-n-ruby-3 text-n-ruby-11'
                    : 'bg-n-amber-3 text-n-amber-11'
                "
              >
                {{
                  isOverdue(payment)
                    ? t('APP_GLOBAL.ACCOUNT_SUSPENDED.STATUS_OVERDUE')
                    : t('APP_GLOBAL.ACCOUNT_SUSPENDED.STATUS_PENDING')
                }}
              </span>
            </div>
            <div class="flex flex-wrap gap-2">
              <NextButton
                v-if="payment.bankSlipUrl"
                sm
                icon="i-hugeicons-file-download"
                :label="t('APP_GLOBAL.ACCOUNT_SUSPENDED.BANK_SLIP')"
                @click="openLink(payment.bankSlipUrl)"
              />
              <NextButton
                sm
                faded
                slate
                icon="i-hugeicons-copy-01"
                :label="t('APP_GLOBAL.ACCOUNT_SUSPENDED.COPY_PIX')"
                :is-loading="copyingPixId === payment.id"
                :disabled="Boolean(copyingPixId)"
                @click="copyPix(payment)"
              />
              <NextButton
                v-if="payment.invoiceUrl"
                sm
                ghost
                slate
                icon="i-hugeicons-link-square-02"
                :label="t('APP_GLOBAL.ACCOUNT_SUSPENDED.INVOICE')"
                @click="openLink(payment.invoiceUrl)"
              />
            </div>
          </li>
        </ul>
        <p
          class="mb-0 border-t border-n-weak px-5 py-3 text-xs text-n-slate-10"
        >
          {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.PAYMENT_HINT') }}
        </p>
      </section>
    </div>
  </div>
</template>
